import jsPDF from 'jspdf';
import TWGoldLoansLogo from '../../../../images/TWGoldLoansLogo.png';

/* ================= HELPERS ================= */

const formatDate = (date) => {
  if (!date) return '-';
  const d = new Date(date);
  return d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const money = (val) =>
  `Rs. ${Number(val || 0).toLocaleString('en-IN')}`;

const loadImageAsBase64 = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      canvas.getContext('2d').drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/jpeg'));
    };

    img.onerror = reject;
    img.src = url;
  });
};

/* ================= MAIN FUNCTION ================= */

export const generateLoanSanctionPDF = async (loan, approvedBy) => {
  const doc = new jsPDF();
  let y = 12;

  /* ================= HEADER ================= */

  // Logo (LEFT)
  doc.addImage(TWGoldLoansLogo, 'PNG', 14, y, 35, 12);

  // RIGHT SIDE IMAGES
  let imgX = 155;
  let imgY = y;

  try {
    if (loan.customerPhoto) {
      const base64 = await loadImageAsBase64(loan.customerPhoto);
      doc.addImage(base64, 'JPEG', imgX, imgY, 28, 28);
      imgY += 32;
    }
  } catch {}

  // Gold images BELOW customer photo
  for (const item of loan.goldItems || []) {
    for (const img of item.images || []) {
      try {
        const base64 = await loadImageAsBase64(img);
        doc.addImage(base64, 'JPEG', imgX, imgY, 28, 18);
        imgY += 22;
        if (imgY > 180) break;
      } catch {}
    }
  }

  y += 18;

  /* ================= SANCTION DETAILS (LEFT BELOW LOGO) ================= */

  doc.setFontSize(10);

  doc.text(`Sanction Date: ${formatDate(new Date())}`, 14, y);
  y += 5;
  doc.text(`Due Date: ${formatDate(loan.nextDueDate)}`, 14, y);
  y += 5;
  doc.text(`CRN: ${loan.customer?.customerId}`, 14, y);
  y += 5;
  doc.text(`GL ID: ${loan.loanAccountNumber}`, 14, y);

  y += 10;

  /* ================= CUSTOMER DETAILS ================= */
 /* ================= DECLARATION SECTION ================= */

// Age calculation
const yob =
loan.customer?.aadhaarDetails?.year_of_birth ||
loan.customer?.aadhaarDetails?.raw_response?.year_of_birth;

const age = yob ? new Date().getFullYear() - yob : '-';

// Care Of (Father / Husband Name)
const rawCareOf =
  loan.customer?.aadhaarDetails?.care_of ||
  loan.customer?.aadhaarDetails?.raw_response?.care_of ||
  '';

const careOf = rawCareOf.replace(/C\/O:?\s*/i, '').trim() || '-';
// Address
const aadhaar = loan.customer?.aadhaarDetails || {};

const address =
aadhaar.full_address ||
aadhaar.raw_response?.full_address ||
aadhaar.raw_response?.address ||
'Address not available';

// ---------------- TEXT ----------------
doc.setFontSize(10);

// Line 1 → Name
doc.text(
  `I/SMT/KUM/MR: ${loan.customer?.name || ''}`,
  14,
  y
);

y += 6;

// Line 2 → Care Of
doc.text(
  `SON/DAUGHTER/WIFE OF SHRI: ${careOf}`,
  14,
  y
);

y += 6;

// Line 3 → Age
doc.text(
  `AGED: ${age}`,
  14,
  y
);

y += 6;

// Address label
doc.text(`RESIDING AT:`, 14, y);
y += 4;

// Address multi-line
const addressLines = doc.splitTextToSize(address, 130);
doc.text(addressLines, 14, y);
y += addressLines.length * 4 + 2;

// Mobile
doc.text(`Mobile No: ${loan.customer?.phone || '-'}`, 14, y);
y += 6;

// Declaration paragraph
doc.text(
`Hereby declare that the below mentioned gold ornaments are owned by me and no person has got any right or encumbrance in respect of the said gold ornaments.`,
14,
y,
{ maxWidth: 180 }
);

y += 10;
  /* ================= GOLD TABLE ================= */

  doc.setFontSize(8);

  const startX = 14;
  const colWidths = [10, 30, 10, 20, 20, 15, 20, 25, 25];

  const headers = [
    'Sl No',
    'Description',
    'Qty',
    'Gross Wt',
    'Net Wt',
    'Purity',
    'Rate/g',
    'Market Value',
    'Loan Amount'
  ];

  let x = startX;

  // Header row
  headers.forEach((h, i) => {
    doc.rect(x, y, colWidths[i], 8);
    doc.text(h, x + 1, y + 5);
    x += colWidths[i];
  });

  y += 8;

  let totalLoanAmount = loan.sanctionedAmount || 0;

  // Rows
  loan.goldItems?.forEach((item, i) => {
    x = startX;

    const row = [
      i + 1,
      item.description || '-',
      '1',
      item.weight || 0,
      item.netWeight || 0,
      loan.goldRateUsed.carat|| '22K',
      loan.goldRateUsed.rate || 0,
      money(item.estimatedValue || 0),
      money(totalLoanAmount)
    ];

    row.forEach((cell, j) => {
      doc.rect(x, y, colWidths[j], 8);
      doc.text(String(cell), x + 1, y + 5);
      x += colWidths[j];
    });

    y += 8;
  });

  // Total
  x = startX;
  doc.text('TOTAL', 140, y + 5);
  doc.text(money(totalLoanAmount), 160, y + 5);

  y += 12;

  /* ================= LOAN DETAILS (NO BOX) ================= */

  const processingFee = loan.processingFee || 566;
  const insurance = loan.insuranceCharges || 45;
  const appraiserFee = loan.appraiserFee || 118;

  const totalDeductions = processingFee + insurance + appraiserFee;
  const netDisbursed = loan.disbursedAmount || (totalLoanAmount - totalDeductions);

  doc.setFontSize(9);

  doc.text(`Loan Amount: ${money(totalLoanAmount)}`, 14, y);
  y += 5;
  doc.text(`Processing Fee: ${money(processingFee)}`, 14, y);
  y += 5;
  doc.text(`Insurance: ${money(insurance)}`, 14, y);
  y += 5;
  doc.text(`Appraiser Fee: ${money(appraiserFee)}`, 14, y);
  y += 6;

  doc.setFont(undefined, 'bold');
  doc.text(`Total Deductions: ${money(totalDeductions)}`, 14, y);
  y += 6;
  doc.text(`Net Disbursement Amount Paid: ${money(netDisbursed)}`, 14, y);
  doc.setFont(undefined, 'normal');

  y += 15;

  /* ================= INTEREST DETAILS ================= */

y += 8;

doc.setFontSize(10);
doc.setFont(undefined, 'bold');
doc.text('Interest Details', 14, y);

doc.setFont(undefined, 'normal');
y += 6;

doc.text(`Interest Rate: ${loan.interestRate}%`, 14, y);
y += 5;

doc.text(`Monthly Interest: ${money(loan.monthlyInterest)}`, 14, y);
y += 5;

doc.text(`Interest Due: 5 of every month`, 14, y);

  /* ================= FOOTER (FIXED AT BOTTOM) ================= */

// Get page height
const pageHeight = doc.internal.pageSize.height;

// Set footer Y position (always bottom)
const footerY = pageHeight - 25;

// Signature lines
doc.line(14, footerY, 60, footerY);
doc.line(75, footerY, 130, footerY);
doc.line(145, footerY, 196, footerY);

// Labels
doc.text('Signature of RM / Asset Officer', 14, footerY + 5);
doc.text('Signature of Branch Manager', 75, footerY + 5);
doc.text("Borrower's Signature", 145, footerY + 5);

// Approved By (below Branch Manager)
doc.text(`Approved By: ${approvedBy?.name || '-'}`, 75, footerY + 12);

  /* ================= PRINT ================= */

  doc.autoPrint();
  window.open(doc.output('bloburl'), '_blank');
};